import { useEffect, useState } from 'react';

import { ErrorApi, bytes as formatoBytes, claveDeCopySubida, duracion, duracionLarga, fecha, hora, shaCorto } from '@haus/core';

import { Boton } from '../componentes/Boton.js';
import { Aviso, BarraAccion, Cabecera, Contenido, Pantalla, Tarjeta } from '../componentes/estructura.js';
import { estilosHoja } from '../componentes/Hoja.js';
import { guardarLimpieza, incidenciasDe, leerLimpieza, type LimpiezaLocal } from '../datos/db.js';
import { useApp } from '../estado/app.js';
import css from './comun.module.css';

/**
 * 07 · Resumen y cierre.
 *
 * La pantalla del "sello de la prueba": lo que convierte unos vídeos en una
 * cadena de custodia. GPS con su precisión, hash, dispositivo y versión de la
 * app, todo a la vista de quien graba, porque quien firma tiene derecho a ver
 * qué firma.
 *
 * **El texto del botón cambia según lo que el móvil pueda cumplir.** En un
 * iPhone no se promete que suba con la app cerrada, porque Safari no lo hace
 * (D3). Prometerlo y que no ocurra es cómo se pierde una prueba: la empleada
 * cierra la app confiada y el vídeo se queda en el teléfono.
 */
export function PantallaResumen({ limpiezaId }: { limpiezaId: string }) {
    const { t, idioma, cliente, cola, ir, atras, capacidades, ajustes, resumenCola, hayRed } = useApp();
    const [l, setL] = useState<LimpiezaLocal | null>(null);
    const [nota, setNota] = useState('');
    const [incidencias, setIncidencias] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [cerrando, setCerrando] = useState(false);

    useEffect(() => {
        void (async () => {
            const limpieza = await leerLimpieza(limpiezaId);
            setL(limpieza);
            setNota(limpieza?.notas ?? '');
            setIncidencias((await incidenciasDe(limpiezaId)).length);
        })();
    }, [limpiezaId]);

    if (l === null) {
        return (
            <Pantalla>
                <Cabecera titulo={t('comun.cargando')} alVolver={atras} />
            </Pantalla>
        );
    }

    const pendientes = l.estancias.filter((e) => e.estado === 'pendiente').length;
    const hechas = l.estancias.filter((e) => e.estado === 'hecha').length;
    const videos = l.estancias.filter((e) => e.video_ms !== null).length;
    const fotos = l.estancias.reduce((n, e) => n + e.fotos, 0);
    const msTotal = l.estancias.reduce((n, e) => n + (e.video_ms ?? 0), 0);
    const bytesTotal = resumenCola?.bytes_pendientes ?? 0;
    const segundos = (Date.now() - new Date(l.started_at).getTime()) / 1000;

    async function finalizar(): Promise<void> {
        if (l === null || pendientes > 0) {
            return;
        }

        setCerrando(true);
        setError(null);

        const fin = new Date().toISOString();
        // Primero el móvil: cerrar tiene que funcionar sin cobertura. El
        // servidor se entera cuando pueda, por la cola.
        await guardarLimpieza({ ...l, finalizada: true, ended_at: fin, notas: nota.trim() || null });

        try {
            await cliente.finalizarLimpieza(l.id, {
                ended_at: fin,
                notas: nota.trim() || null,
                gps: l.gps,
                estancias: l.estancias.map((e) => ({
                    id: e.id,
                    estado: e.estado,
                    motivo_no_accesible: e.motivo_no_accesible,
                    video_ms: e.video_ms,
                })),
            });
        } catch (e) {
            // Que no se pueda avisar al servidor NO impide cerrar: la limpieza
            // está cerrada en el móvil y la cola la sincronizará. Lo único que
            // se hace es no mentir sobre el estado.
            if (!(e instanceof ErrorApi) || !e.reintentable) {
                setError(t('error.inesperado'));
            }
        }

        void cola.arrancar('limpieza finalizada');
        setCerrando(false);
        // 'raiz': una limpieza cerrada no se vuelve a visitar con el botón
        // atrás. Lo que hay detrás es el día, y punto.
        ir({ n: 'hoy' }, 'raiz');
    }

    const claveCopy = claveDeCopySubida(capacidades, ajustes.solo_wifi, hayRed);

    return (
        <Pantalla>
            <Cabecera titulo={t('resumen.titulo')} alVolver={atras} />

            <Contenido>
                <div className={css.centro}>
                    <span className={css.circuloOkGrande}>✓</span>
                    <h1 className={css.titulo}>{t('resumen.titulo')}</h1>
                    <p className={css.parrafo}>
                        {t('resumen.subtitulo', {
                            apartamento: l.apartamento,
                            fecha: fecha(l.started_at, idioma),
                            hora: hora(l.started_at, idioma),
                            duracion: duracionLarga(segundos),
                        })}
                    </p>
                </div>

                <Tarjeta>
                    <div className={css.recuento}>
                        <Fila etiqueta={t('resumen.estancias')} valor={`${hechas} ${t('comun.de')} ${l.estancias.length}`} />
                        <Fila etiqueta={t('resumen.videos')} valor={`${videos} · ${duracion(msTotal)}`} />
                        <Fila etiqueta={t('resumen.fotos')} valor={String(fotos)} />
                        <Fila etiqueta={t('resumen.tamano')} valor={formatoBytes(bytesTotal, idioma)} />
                        <div className={css.separador} />
                        <Fila
                            etiqueta={t('resumen.incidencias')}
                            valor={String(incidencias)}
                            tono={incidencias > 0 ? 'warn' : undefined}
                        />
                    </div>
                </Tarjeta>

                <Tarjeta>
                    <span className="etiqueta-mono">{t('resumen.sello')}</span>
                    <div className={css.bloqueMono} style={{ background: 'transparent', padding: 0 }}>
                        <div className={css.filaMono}>
                            <span>gps</span>
                            <span>
                                {l.gps === null
                                    ? t('resumen.sello_sin_gps')
                                    : t('resumen.sello_gps', {
                                          lat: l.gps.lat.toFixed(5),
                                          lng: l.gps.lng.toFixed(5),
                                          precision: Math.round(l.gps.acc),
                                      })}
                            </span>
                        </div>
                        <div className={css.filaMono}>
                            <span>limpieza</span>
                            <span>{shaCorto(l.id)}</span>
                        </div>
                        <div className={css.filaMono}>
                            <span>dispositivo</span>
                            <span>
                                {t('resumen.sello_dispositivo', {
                                    dispositivo: capacidades.esIos ? 'iOS' : 'Android',
                                    version: __VERSION__,
                                })}
                            </span>
                        </div>
                    </div>
                </Tarjeta>

                <div>
                    <span className="etiqueta-mono">{t('resumen.nota')}</span>
                    <textarea
                        className={estilosHoja.campo}
                        style={{ marginTop: 'var(--e8)' }}
                        placeholder={t('resumen.nota_ejemplo')}
                        value={nota}
                        maxLength={1000}
                        onChange={(e) => setNota(e.target.value)}
                    />
                </div>

                {pendientes > 0 && (
                    <Aviso>
                        {t('resumen.pendientes', { n: pendientes })} · {t('resumen.pendientes_ayuda')}
                    </Aviso>
                )}

                {error !== null && <Aviso>{error}</Aviso>}
            </Contenido>

            <BarraAccion nota={t(claveCopy)}>
                <Boton disabled={pendientes > 0 || cerrando} onClick={() => void finalizar()}>
                    {t('resumen.finalizar')}
                </Boton>
            </BarraAccion>
        </Pantalla>
    );
}

function Fila({ etiqueta, valor, tono }: { etiqueta: string; valor: string; tono?: 'warn' | undefined }) {
    return (
        <div className={css.recuentoFila}>
            <span className={css.recuentoEtiqueta}>{etiqueta}</span>
            <span className={css.recuentoValor} style={tono === 'warn' ? { color: 'var(--warn-ink)' } : undefined}>
                {valor}
            </span>
        </div>
    );
}
